/**
 * CardHeader Component - 卡片頭部區域
 * Requirements: 7.5
 * 
 * 深色背景設計，顯示商品類型標籤、線路標籤、廠商、產品名稱、地區、備註
 */

import { MapPinIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import type { CardData, CategoryType } from '../../types/card';
import type { Translations } from '../../i18n';

interface CardHeaderProps {
  cardData: CardData;
  t: Translations;
}

/** 取得商品類型的標籤 */
function getCategoryLabel(category: CategoryType, t: Translations): string {
  const labels: Record<CategoryType, string> = {
    vps: t.categoryVps,
    vds: t.categoryVds,
    hosting: t.categoryHosting,
    account: t.categoryAccount,
    domain: t.categoryDomain,
  };
  return labels[category] || category;
}

/** 取得商品類型的背景顏色 */
function getCategoryColor(category: CategoryType): string {
  const colors: Record<CategoryType, string> = {
    vps: 'bg-blue-500',
    vds: 'bg-purple-500',
    hosting: 'bg-green-500',
    account: 'bg-orange-500',
    domain: 'bg-pink-500',
  };
  return colors[category] || 'bg-gray-500';
}

/** 判斷是否需要顯示線路（VPS/VDS 需要，虛擬主機不需要） */
function needsRouteType(category: CategoryType): boolean {
  return category === 'vps' || category === 'vds';
}

export function CardHeader({ cardData, t }: CardHeaderProps) {
  const { category, provider, productName, region, remarks, routeType, customRoute } = cardData;
  const showRoute = needsRouteType(category);
  const displayRoute = routeType === 'custom' ? customRoute : routeType;

  return (
    <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-4 rounded-t-lg">
      {/* 標籤區域 */}
      <div className="flex flex-wrap gap-2 mb-3">
        {/* 商品類型標籤 */}
        <span className={`${getCategoryColor(category)} px-2 py-1 rounded text-xs font-medium`}>
          {getCategoryLabel(category, t)}
        </span>
        
        {/* 線路標籤 (僅伺服器類型顯示) */}
        {showRoute && displayRoute && (
          <span className="bg-cyan-600 px-2 py-1 rounded text-xs font-medium">
            {displayRoute}
          </span>
        )}
      </div>

      {/* 廠商和產品名稱 */}
      <div className="mb-2">
        <h2 className="text-lg font-bold">
          {category === 'domain' ? productName : `${provider} - ${productName}`}
        </h2>
        {category === 'domain' && provider && (
          <p className="text-slate-300 text-sm">{t.registrar}：{provider}</p>
        )}
      </div>

      {/* 地區 (非域名類型顯示) */}
      {category !== 'domain' && region && (
        <div className="flex items-center gap-1 text-slate-300 text-sm mb-1">
          <MapPinIcon className="w-4 h-4" />
          <span>{region}</span>
        </div>
      )}

      {/* 備註 */}
      {remarks && (
        <div className="mt-2 text-slate-300 text-sm italic flex items-start gap-1">
          <DocumentTextIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{remarks}</span>
        </div>
      )}
    </div>
  );
}

export default CardHeader;
